/**
 * Hook that injects pixel scripts on mount and fires Meta / TikTok / GTM
 * pixel events alongside the WAE analytics path.
 *
 * The funnel SPA uses its own 15-event taxonomy (`FunnelAnalyticsEventName`),
 * while the pixel adapters in `@nextday/tracking` consume the 10-event
 * `EventName` type. This hook maps between the two and builds the
 * `EventEnvelope` the adapters expect.
 */

import { useCallback, useEffect, useRef } from "react";
import { getLandingHref } from "@nextday/attribution";
import { wrapEnvelope, type EventName, type FunnelEvent } from "@nextday/tracking";
import { fireMetaPixel } from "@nextday/tracking/pixels/meta";
import { fireTikTokPixel } from "@nextday/tracking/pixels/tiktok";
import { fireGtmPixel } from "@nextday/tracking/pixels/gtm";
import type { FunnelAnalyticsEventName } from "@src/funnel/hooks/useFunnelAnalytics";
import { readPixelConfig, type PixelConfig } from "@src/tracking/pixelConfig";
import {
  buildGa4PageLocation,
  injectGtag,
  injectPixelScripts,
} from "@src/tracking/injectPixelScripts";

/**
 * Maps the SPA's 15-event taxonomy to the tracking package's 10-event
 * taxonomy. Events not in this map are WAE-only — no pixel fires.
 */
const EVENT_NAME_MAP: Partial<Record<FunnelAnalyticsEventName, EventName>> = {
  funnel_landing_viewed: "funnel_landed",
  funnel_step_viewed: "funnel_step_viewed",
  funnel_step_completed: "funnel_step_submitted",
  funnel_back_clicked: "funnel_step_skipped",
  funnel_abandoned: "funnel_dropped",
  funnel_signup_completed: "funnel_account_created",
  funnel_handoff_clicked: "funnel_handoff_clicked",
  purchase_completed: "funnel_paid_conversion",
};

export type PixelTrackingContext = {
  readonly sessionId: string;
  readonly anonymousId: string;
  readonly funnelId: string;
  readonly overrideId: string | null;
  readonly applicationId: string;
  readonly ga4MeasurementId: string | null;
  readonly analyticsEnabled: boolean;
  readonly isPreview: boolean;
};

export type PixelFireFn = (
  event: FunnelAnalyticsEventName,
  payload?: Record<string, unknown>,
) => void;

export const usePixelTracking = (context: PixelTrackingContext): PixelFireFn => {
  const contextRef = useRef(context);
  contextRef.current = context;

  const configRef = useRef<PixelConfig | null>(null);

  useEffect(() => {
    const config = readPixelConfig();
    configRef.current = config;
    injectPixelScripts(config);
    const measurementId = contextRef.current.ga4MeasurementId;
    if (
      contextRef.current.analyticsEnabled === true &&
      contextRef.current.isPreview !== true &&
      typeof measurementId === "string" &&
      measurementId.length > 0
    ) {
      injectGtag(measurementId, buildGa4PageLocation(getLandingHref(), window.location.href));
    }
  }, []);

  return useCallback((event: FunnelAnalyticsEventName, payload: Record<string, unknown> = {}) => {
    const trackingName = EVENT_NAME_MAP[event];
    if (trackingName === undefined) return;

    const ctx = contextRef.current;
    const funnelEvent: FunnelEvent = {
      name: trackingName,
      context: {
        onboarding_session_id: ctx.sessionId,
        anonymous_id: ctx.anonymousId,
        funnel_id: ctx.funnelId,
        ...(ctx.overrideId !== null ? { override_id: ctx.overrideId } : {}),
        application_id: ctx.applicationId,
      },
      payload,
      occurred_at: Date.now(),
    };

    const envelope = wrapEnvelope(funnelEvent);
    const config = configRef.current;
    if (config?.metaPixelId) fireMetaPixel(envelope);
    if (config?.tiktokPixelId) fireTikTokPixel(envelope);
    if (config?.gtmContainerId) fireGtmPixel(envelope);
  }, []);
};
